import Header from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, AlertTriangle, Scale, Users } from "lucide-react";

const Terms = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-4">Terms of Service</Badge>
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">Terms of Service</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Please read these Terms of Service carefully before using Soundfield. By accessing our website, you agree to be bound by these terms.
          </p>
          <p className="text-sm text-muted-foreground mt-4">
            Last updated: December 2024
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-8">
          {/* Acceptance of Terms */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Acceptance of Terms
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                By accessing and using Soundfield ("the Site"), you accept and agree to be bound by the terms and provision of this agreement. These Terms of Service govern your use of our website and services.
              </p>
              
              <p className="text-muted-foreground">
                If you do not agree to abide by the terms and conditions of this agreement, you are not authorized to use or access this website.
              </p>
            </CardContent>
          </Card>

          {/* Use of the Website */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Use of the Website
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Permitted Use</h3>
                <p className="text-muted-foreground">
                  You may use our website for lawful purposes only. You agree to use the website in a manner consistent with any and all applicable laws and regulations.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">Prohibited Activities</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Using the website for any unlawful purpose or in violation of these Terms</li>
                  <li>• Attempting to gain unauthorized access to any portion of the website</li>
                  <li>• Interfering with or disrupting the website or servers</li>
                  <li>• Impersonating any person or entity</li>
                  <li>• Posting or transmitting harmful, offensive, or inappropriate content</li>
                  <li>• Violating the intellectual property rights of others</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Content and Intellectual Property */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scale className="h-5 w-5" />
                Content and Intellectual Property
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Our Content</h3>
                <p className="text-muted-foreground">
                  All content on Soundfield, including but not limited to articles, reviews, images, graphics, logos, and design elements, is owned by Soundfield or our content providers and is protected by copyright, trademark, and other intellectual property laws.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">Limited License</h3>
                <p className="text-muted-foreground">
                  We grant you a limited, non-exclusive, non-transferable license to access and use our website for personal, non-commercial purposes. You may not reproduce, distribute, modify, or create derivative works from our content without written permission.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">User Submissions</h3>
                <p className="text-muted-foreground">
                  If you submit content to us (such as comments, messages, or other materials), you grant us a non-exclusive, worldwide, royalty-free license to use, reproduce, and publish such content in connection with our services.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Privacy */}
          <Card>
            <CardHeader>
              <CardTitle>Privacy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Your privacy is important to us. Our Privacy Policy, which is incorporated into these Terms by reference, explains how we collect, use, and protect your information. By using our website, you consent to our privacy practices as described in our{" "}
                <a href="/privacy" className="text-primary hover:underline">
                  Privacy Policy
                </a>.
              </p>
            </CardContent>
          </Card>

          {/* Disclaimers */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Disclaimers and Limitations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Website Availability</h3>
                <p className="text-muted-foreground">
                  We strive to keep our website available at all times, but we do not guarantee uninterrupted access. The website may be temporarily unavailable due to maintenance, technical issues, or other factors beyond our control.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">Content Accuracy</h3>
                <p className="text-muted-foreground">
                  While we make reasonable efforts to ensure the accuracy of our content, we do not guarantee that all information on the website is complete, accurate, or up-to-date. All content is provided for informational purposes only.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">Third-Party Links</h3>
                <p className="text-muted-foreground">
                  Our website may contain links to third-party websites. We are not responsible for the content, privacy practices, or terms of service of external websites. Links do not constitute endorsement of the linked sites.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Limitation of Liability */}
          <Card>
            <CardHeader>
              <CardTitle>Limitation of Liability</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                To the fullest extent permitted by law, Soundfield and its affiliates, officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or other intangible losses.
              </p>
              
              <p className="text-muted-foreground">
                In no event shall our total liability to you exceed the amount you have paid us, if any, for accessing our website in the twelve months preceding the event giving rise to liability.
              </p>
            </CardContent>
          </Card>

          {/* Modifications and Termination */}
          <Card>
            <CardHeader>
              <CardTitle>Modifications and Termination</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Changes to Terms</h3>
                <p className="text-muted-foreground">
                  We reserve the right to modify these Terms of Service at any time. Changes will be posted on this page with an updated revision date. Your continued use of the website after changes are posted constitutes acceptance of the revised terms.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">Termination</h3>
                <p className="text-muted-foreground">
                  We may terminate or suspend your access to our website at any time, without prior notice or liability, for any reason, including if you breach these Terms of Service.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                If you have any questions about these Terms of Service, please contact us through our{" "}
                <a href="/contact" className="text-primary hover:underline">
                  Contact page
                </a>.
              </p>
              
              <p className="text-muted-foreground">
                These Terms of Service constitute the entire agreement between you and Soundfield regarding the use of our website and supersede all prior agreements and understandings.
              </p>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-muted/30 py-12">
        <div className="container">
          <div className="text-center text-sm text-muted-foreground">
            © 2024 Soundfield. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Terms;